import { matchTrackerApi } from '../../modules/servers/api'
import type {
  DrillPlayerBreakdownArgs,
  RawPlayerBreakdowns
} from '../drills/types'
import type { SessionPlayerBreakdownArgs } from '../sessions/types'
import type { MetricBands, SelectedMetricBand } from './types'

// No bands → omit the params entirely for the full, unbanded breakdown.
// Otherwise one distinct query key per metric (`<paramKey>=<id>`), which RTK
// Query serialises natively — no paramsSerializer needed. The unit is never put
// on the wire (it's implied by the band id).
const bandParams = (bands?: SelectedMetricBand[]) =>
  bands?.length
    ? Object.fromEntries(bands.map((b) => [b.paramKey, b.id]))
    : undefined

// All player-metric endpoints share the new backend `playerMetrics` router and
// live together on matchTrackerApi. The two breakdowns are tagged by id only, so
// every band variant refetches on the same invalidations as the unbanded
// breakdown (tags invalidated from events/api.ts, events/thunks.ts, and the
// drill create/update mutations in drills/api.ts).
export const playerMetricsMatchTrackerApi = matchTrackerApi.injectEndpoints({
  endpoints: (builder) => ({
    getSessionPlayerBreakdown: builder.query<
      RawPlayerBreakdowns,
      SessionPlayerBreakdownArgs
    >({
      query: ({ sessionId, bands }) => ({
        url: `/playerMetrics/sessions/${sessionId}`,
        method: 'GET',
        params: bandParams(bands),
        withCredentials: true
      }),
      providesTags: (result, error, { sessionId }) => [
        { type: 'sessionPlayerBreakdown', id: sessionId }
      ]
    }),

    getDrillPlayerBreakdown: builder.query<
      RawPlayerBreakdowns,
      DrillPlayerBreakdownArgs
    >({
      query: ({ drillId, bands }) => ({
        url: `/playerMetrics/drills/${drillId}`,
        method: 'GET',
        params: bandParams(bands),
        withCredentials: true
      }),
      providesTags: (result, error, { drillId }) => [
        { type: 'drillPlayerBreakdown', id: drillId }
      ]
    }),

    // Discovery for breakdown band filters — a pure function of a static
    // backend registry, so one fetch per app load is enough (no tags needed)
    getMetricBands: builder.query<MetricBands, void>({
      query: () => ({
        url: `/playerMetrics/bands`,
        method: 'GET',
        withCredentials: true
      })
    })
  })
})

export const {
  useGetSessionPlayerBreakdownQuery,
  useGetDrillPlayerBreakdownQuery,
  useGetMetricBandsQuery
} = playerMetricsMatchTrackerApi
